import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCoreRowModel } from '@tanstack/react-table';
import { Autocomplete, Box, Paper, TextField, Typography } from '@mui/material';
import { ColumnDef } from '@tanstack/table-core';

// Utils
import { Storage } from '../../../../utils/Storage';

// Redux
import { useAppDispatch, useAppSelector } from '../../../../redux/hook';
import { fetchAllSettings } from '../../../../redux/slices/settingsSlice';

// Types
import { FleetSummary } from '../../../../types/Fleetsummary';
import { FilterableTypes } from '../../../../types/FilterableTypes';
import { AppConfig } from '../../../../constants/Constants';

// Common components
import { Table } from '../../../common/table/index';
import SearchBar from '../../../common/SearchBar/SearchBar';
import FormInputCollection from '../../../common/FormInputCollection/FormInputCollection';

interface FleetFilterPanelProps {
  table: any;
  columns: ColumnDef<FleetSummary>[];
  paramFilter: { filters: any[] };
  setParamFilter: (next: { filters: any[] }) => void;
  onReset?: () => void;
  selectedFacilityId?: string | null;
}

const MAP_HEIGHT = 520;
const ALL = '__ALL__';

type SearchableOption = { value: string; label: string };
const EMPTY_OPTION: SearchableOption = { value: '', label: '' };

const searchableFilterSx = {
  width: '100%',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
};

export default function FleetFilterPanel({ table, columns, paramFilter, setParamFilter, onReset, selectedFacilityId }: FleetFilterPanelProps) {
  const dispatch = useAppDispatch();
  const settingStates = useAppSelector((state) => state.settings);

  useEffect(() => {
    dispatch(fetchAllSettings());
  }, [dispatch]);

  const restored = useMemo(() => Storage.getFleetDashboardState(), []);

  const [equipmentType, setEquipmentType] = useState<string>(restored?.equipmentType ?? ALL);
  const [facilityType, setFacilityType] = useState<string>(restored?.facilityType ?? ALL);

  const equipmentTypeRef = useRef(equipmentType);
  const facilityTypeRef = useRef(facilityType);

  const initialState = useMemo(
    () => ({
      pagination: {
        pageIndex: restored?.pagination?.pageIndex ?? 0,
        pageSize: restored?.pagination?.pageSize ?? AppConfig.defaultPageSize,
      },
    }),
    [restored],
  );

  const handlePaginationChange = useCallback((pagination: { pageIndex: number; pageSize: number }) => {
    Storage.setFleetDashboardState({ pagination });
  }, []);

  const handleEquipmentTypeChange = useCallback((value: string) => {
    equipmentTypeRef.current = value;
    setEquipmentType(value);
    Storage.setFleetDashboardState({ equipmentType: value });
  }, []);

  const handleFacilityTypeChange = useCallback((value: string) => {
    facilityTypeRef.current = value;
    setFacilityType(value);
    Storage.setFleetDashboardState({ facilityType: value });
  }, []);

  const searchableEquipmentOptions = useMemo<SearchableOption[]>(() => {
    const opts = (settingStates.equipmentTypes?.value ?? [])
      .map((v: { text: string; value: string }) => ({ value: v.text, label: v.value }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: ALL, label: 'All' }, ...opts];
  }, [settingStates.equipmentTypes]);

  const facilityTypeOptions = useMemo<SearchableOption[]>(() => {
    const opts = (settingStates.facilityTypes?.value ?? [])
      .map((v: { text: string; value: string }) => ({ value: String(v.text), label: v.value }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: ALL, label: 'All' }, ...opts];
  }, [settingStates.facilityTypes]);

  const handleSearch = useCallback(
    (searchFilters: any[]) => {
      const finalFilters = [...(searchFilters ?? [])];

      if (facilityTypeRef.current !== ALL) {
        finalFilters.push({
          facilityType: Number(facilityTypeRef.current),
          filterType: FilterableTypes.Equals,
        });
      }

      if (equipmentTypeRef.current !== ALL) {
        finalFilters.push({
          type: Number(equipmentTypeRef.current),
          filterType: FilterableTypes.Equals,
        });
      }

      setParamFilter({ filters: finalFilters });
      Storage.setFleetDashboardState({ filters: finalFilters });
    },
    [setParamFilter],
  );

  const handleReset = useCallback(() => {
    equipmentTypeRef.current = ALL;
    facilityTypeRef.current = ALL;
    setEquipmentType(ALL);
    setFacilityType(ALL);
    setParamFilter({ filters: [] });
    Storage.clearFleetDashboardState();
    onReset?.();
  }, [setParamFilter, onReset]);

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: '#fff !important',
        boxShadow: 'none !important',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        height: MAP_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
        <Typography variant='subtitle1' fontWeight={700}>
          Station & Facility Filter
        </Typography>
      </Box>

      <Box sx={{ px: 1, pt: 1, backgroundColor: 'background.default' }}>
        <SearchBar
          pageKey='GLOBAL_FLEET_FACILITY_FILTER'
          columns={columns}
          onSearch={handleSearch}
          onReset={handleReset}
          allowSearchAll
          defaultSearchColumn='stationIataCode'
          components={
            <Box sx={{ display: 'flex', gap: 1.5, px: 0, flexWrap: 'wrap' }}>
              <FormInputCollection label='Equipment Type' style={{ width: 170 }}>
                <Autocomplete
                  id='fleet-equipment-type'
                  options={searchableEquipmentOptions}
                  value={searchableEquipmentOptions.find((o) => o.value === equipmentType) ?? EMPTY_OPTION}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  getOptionLabel={(option) => option.label}
                  onChange={(_, option) => handleEquipmentTypeChange(option?.value ?? ALL)}
                  size='small'
                  disableClearable
                  sx={{ ...searchableFilterSx, width: 170 }}
                  ListboxProps={{ sx: { maxHeight: 320, '& .MuiAutocomplete-option': { minHeight: 40, fontSize: 12 } } }}
                  renderInput={(params) => <TextField {...params} placeholder='All' />}
                />
              </FormInputCollection>

              <FormInputCollection label='Facility Type' style={{ width: 170 }}>
                <Autocomplete
                  id='fleet-facility-type'
                  options={facilityTypeOptions}
                  value={facilityTypeOptions.find((o) => o.value === facilityType) ?? EMPTY_OPTION}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  getOptionLabel={(option) => option.label}
                  onChange={(_, option) => handleFacilityTypeChange(option?.value ?? ALL)}
                  size='small'
                  disableClearable
                  sx={{ ...searchableFilterSx, width: 170 }}
                  ListboxProps={{ sx: { maxHeight: 320, '& .MuiAutocomplete-option': { minHeight: 40, fontSize: 12 } } }}
                  renderInput={(params) => <TextField {...params} placeholder='All' />}
                />
              </FormInputCollection>
            </Box>
          }
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          backgroundColor: '#fff !important',
          flexDirection: 'column',
          '& .ui-table__toolbar': {
            padding: '0 !important',
          },
          '& .ui-table': { height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' },
          '& .ui-table__card': { flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', boxShadow: 'none', border: 'none' },
          '& .ui-table__scroll': { flex: '1 1 auto', minHeight: 0, maxHeight: 'none !important', overflowY: 'auto !important' },
          '& .ui-table__pagination': { flex: '0 0 auto' },
          '& tbody tr': { cursor: 'pointer' },
          ...(selectedFacilityId && {
            [`& tbody tr[data-facility-id="${selectedFacilityId}"]`]: {
              backgroundColor: '#ede9fe !important',
            },
          }),
        }}
      >
        <Table
          {...table}
          columns={columns}
          initialState={initialState}
          onPaginationStateChange={handlePaginationChange}
          showSearchBar={false}
          showColumnToggler={false}
          showAction={false}
          getCoreRowModel={getCoreRowModel()}
          serverSide
          rowProps={(row: { original: FleetSummary }) => ({ 'data-facility-id': row.original.facilityId })}
        />
      </Box>
    </Paper>
  );
}
